import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { dasa } from 'src/classess/dasaClasses';

/** Sections of the storefront that can carry their own palette. */
export type ThemePage = 'general' | 'ordering' | 'reservation' | 'catering';

/**
 * One themeable colour.
 *
 * `key`    — name used in uiSettings.pageThemesJson
 * `cssVar` — the custom property components read
 * `legacy` — the dedicated uisettings column this token used to live in
 * `from`   — default, which may reference an already-resolved token
 */
interface TokenDef {
  key: string;
  cssVar: string;
  legacy?: string;
  from?: (get: (k: string) => string) => string;
}

/**
 * Resolves the store's palette to CSS custom properties, per page.
 *
 * <p>Two problems this solves.
 *
 * <p><b>Dark backgrounds.</b> The storefront carried ~59 hardcoded white text
 * declarations, because components assumed dark chrome behind them. Making a
 * background light therefore made its text vanish, and no setting could reach
 * it. Every background token here now has a paired foreground token
 * ({@code …TextColor}), so text is defined relative to what it sits on.
 *
 * <p><b>Per-page palettes.</b> A store may want its booking flow to look
 * different from its catering menu. Tokens resolve
 * page → general → legacy column → built-in default, so a page overrides only
 * what it cares about and a store that has configured nothing renders exactly
 * as it did before any of this existed.
 */
@Injectable({ providedIn: 'root' })
export class StorefrontThemeService {

  /** Canonical token list. Adding a colour means adding one row here. */
  private static readonly TOKENS: TokenDef[] = [
    // Chrome
    { key: 'headerColor',        cssVar: '--header-color',      legacy: 'header',   from: () => '#034131' },
    { key: 'headerTextColor',    cssVar: '--header-text-color',                     from: () => '#ffffff' },
    { key: 'footerColor',        cssVar: '--footer-color',      legacy: 'footer',   from: () => '#241C15' },
    { key: 'footerTextColor',    cssVar: '--footer-text-color',                     from: () => '#ffffff' },

    // Page + body text
    { key: 'bodyBgColor',        cssVar: '--body-bg-color',     legacy: 'primaryColor', from: () => '#d8eacc' },
    { key: 'bodyTextColor',      cssVar: '--body--text-color-color', legacy: 'bodyText', from: () => '#000000' },
    { key: 'mutedTextColor',     cssVar: '--muted-text-color',  legacy: 'mutedTextColor', from: () => '#374151' },
    { key: 'headingTextColor',   cssVar: '--heading-text-color', legacy: 'headingTextColor', from: g => g('bodyTextColor') },
    { key: 'borderColor',        cssVar: '--border-color',      legacy: 'borderColor', from: () => '#e5e7eb' },

    // Surfaces
    { key: 'secondaryBgColor',   cssVar: '--secondary-bg-color', legacy: 'secondaryColor', from: () => '#ffffff' },
    { key: 'secondaryTextColor', cssVar: '--secondary-text-color', from: g => g('bodyTextColor') },
    { key: 'surfaceColor',       cssVar: '--surface-color',     legacy: 'surfaceColor', from: g => g('secondaryBgColor') },
    { key: 'surfaceTextColor',   cssVar: '--surface-text-color', from: g => g('bodyTextColor') },
    { key: 'pageBannerColor',    cssVar: '--page-banner-color', legacy: 'pageBannerColor', from: g => g('secondaryBgColor') },
    { key: 'pageBannerTextColor',cssVar: '--page-banner-text-color', from: g => g('bodyTextColor') },
    { key: 'menuBarColor',       cssVar: '--menu-bar-color',    legacy: 'menuBarColor', from: g => g('headerColor') },
    { key: 'menuBarTextColor',   cssVar: '--menu-bar-text-color', from: g => g('headerTextColor') },

    // Accent + prices
    { key: 'accentColor',        cssVar: '--accent-color',      legacy: 'accentColor', from: g => g('okButtonColor') },
    { key: 'accentContrastColor',cssVar: '--accent-contrast-color', legacy: 'accentContrastColor', from: () => '#ffffff' },
    { key: 'priceColor',         cssVar: '--price-color',       legacy: 'priceColor', from: g => g('bodyTextColor') },

    // Buttons and navigation
    { key: 'okButtonColor',      cssVar: '--ok-button-color',   legacy: 'buttonSaveOk', from: () => '#00046B' },
    { key: 'okButtonTextColor',  cssVar: '--ok-button-text-color', from: () => '#ffffff' },
    { key: 'cancelButtonColor',  cssVar: '--cancel-button-color', legacy: 'cancelButton', from: () => '#dc3545' },
    { key: 'cancelButtonTextColor', cssVar: '--cancel-button-text-color', from: () => '#ffffff' },
    { key: 'menuButtonColor',    cssVar: '--menu-button-color', legacy: 'checkOutColor', from: () => '#25F6AF' },
    { key: 'menuButtonTextColor',cssVar: '--menu-button-text-color', from: () => '#000000' },
    { key: 'catButtonColor',     cssVar: '--cat-button-color',  legacy: 'categoryButton', from: () => '#deb887' },
    { key: 'catButtonTextColor', cssVar: '--cat-button-text-color', from: () => '#000000' },
    { key: 'leftMenuColor',      cssVar: '--left-menu-color',   legacy: 'leftNavigationButton', from: () => '#ffffff' },
    { key: 'leftMenuTextColor',  cssVar: '--left-menu-text-color', from: () => '#000000' },
  ];

  private currentPage: ThemePage = 'general';

  constructor(private router: Router) {
    // Re-apply on navigation: CSS custom properties are global, so moving from
    // a page with overrides to one without has to reset them rather than leave
    // the previous page's palette behind.
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const page = StorefrontThemeService.pageForUrl(e.urlAfterRedirects || e.url || '');
        if (page !== this.currentPage) {
          this.currentPage = page;
          this.apply();
        }
      });
  }

  /** Maps a route to the section whose palette should apply. */
  static pageForUrl(url: string): ThemePage {
    const u = (url || '').toLowerCase();
    if (u.includes('catering')) return 'catering';
    if (u.includes('reservation')) return 'reservation';
    if (u.includes('store-item') || u.includes('storeitem') || u.includes('placeorder')
      || u.includes('order') || u.includes('cart') || u.includes('checkout')
      || u.includes('menu')) return 'ordering';
    return 'general';
  }

  /**
   * Publishes the palette for the current page. Safe to call repeatedly.
   */
  apply(settings?: any, page?: ThemePage): void {
    const s = settings ?? dasa.Utility.uiSettings ?? {};
    const target: ThemePage = page ?? this.currentPage;

    const overrides = StorefrontThemeService.pageOverrides(s, target);
    const general = StorefrontThemeService.pageOverrides(s, 'general');

    const clean = (v: any) => (typeof v === 'string' && v.trim() !== '') ? v.trim() : null;
    const resolved: { [k: string]: string } = {};
    const inFlight = new Set<string>();

    // Resolved lazily rather than in array order, because some defaults refer
    // to other tokens (accent falls back to the Save/OK colour, card text to
    // body text). Ordering the array by hand would silently produce an empty
    // colour the first time someone moved a row; this cannot.
    const resolveToken = (key: string): string => {
      if (resolved[key] !== undefined) return resolved[key];
      const t = StorefrontThemeService.TOKENS.find(x => x.key === key);
      if (!t) return '';
      if (inFlight.has(key)) return '';   // guards a cyclic default
      inFlight.add(key);
      const value =
        clean(overrides[t.key]) ??
        clean(general[t.key]) ??
        (t.legacy ? clean(s[t.legacy]) : null) ??
        (t.from ? t.from(resolveToken) : '');
      inFlight.delete(key);
      resolved[key] = value;
      return value;
    };

    for (const t of StorefrontThemeService.TOKENS) {
      document.documentElement.style.setProperty(t.cssVar, resolveToken(t.key));
    }

    // Aliases for the older second naming scheme still referenced in places.
    document.documentElement.style.setProperty('--brand', resolved['accentColor']);
    document.documentElement.style.setProperty('--text', resolved['bodyTextColor']);
    document.documentElement.style.setProperty('--body-bg', resolved['bodyBgColor']);
    document.documentElement.style.setProperty('--border', resolved['borderColor']);
    document.documentElement.style.setProperty('--muted', resolved['mutedTextColor']);

    const perRow = Number(s.cateringItemsPerRow);
    document.documentElement.style.setProperty(
      '--catering-items-per-row', String(perRow >= 2 && perRow <= 5 ? perRow : 3));
  }

  /** Overrides stored for one page; {} when absent or unparseable. */
  static pageOverrides(settings: any, page: ThemePage): any {
    const raw = settings ? settings.pageThemesJson : null;
    if (!raw) return {};
    try {
      const parsed = (typeof raw === 'string') ? JSON.parse(raw) : raw;
      return (parsed && parsed[page]) ? parsed[page] : {};
    } catch {
      // A malformed blob must never take the storefront's colours down.
      return {};
    }
  }

  /** Token metadata, for anything that needs to enumerate the palette. */
  static get tokens(): TokenDef[] {
    return StorefrontThemeService.TOKENS;
  }
}
